import { FC, PropsWithChildren, createContext, useCallback, useMemo, useState } from "react";
import { useDidUpdateEffect, usePrevious } from "../hooks";
import { BrowserStorageIdsEnum } from "./browser.storage.provider";

export interface IAuthContext {
    setToken: (token: string | null) => void;
    userToken: string | null;
    prevToken?: string | null;
    isUserLoggedIn: boolean;
}

export const AuthContext = createContext<IAuthContext>(null as any);

type Props = {
    initialJwt: string;
};

export const AuthProvider: FC<Props & PropsWithChildren> = ({ children, initialJwt }) => {
    const [jwt, setJwt] = useState<string | null>(initialJwt || null);
    const prevToken = usePrevious(jwt);

    const setToken = useCallback((token: string | null) => {
        if (!token) {
            localStorage.removeItem(BrowserStorageIdsEnum.TOKEN);
            setJwt(null);
        } else {
            localStorage.setItem(BrowserStorageIdsEnum.TOKEN, token);
            setJwt(token);
        }
    }, []);

    useDidUpdateEffect(() => {
        if (initialJwt !== jwt) {
            setToken(initialJwt);
        }
    }, [initialJwt]);

    const value = useMemo(
        () => ({
            setToken,
            userToken: jwt,
            prevToken,
            isUserLoggedIn: jwt !== null && !!jwt,
        }),
        [jwt, setToken, prevToken],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
